import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import { getConcretagemRealizada } from '../services/concretagemRealizadaService';
import { getObras } from '../services/obraService';
import { getRevisaoResultado, salvarRevisaoResultado } from '../services/resultadoCorpoProvaService';
import { getSessionRole } from '../services/authSession';
import { calcularFcj, calcularFcjEsperado, calcularFcmj } from '../utils/fcjCalculo';
import { formatDateBr, normalizeDateOnly } from '../utils/formatters';
import { showSuccessToast } from '../utils/notifications';
import useSubmitLock from '../hooks/useSubmitLock';

const INITIAL_FILTROS = { concretagem: '', caminhao: '' };

// Formata um número no padrão pt-BR com até `fractionDigits` casas (ou '-' se inválido).
function formatDecimal(value, fractionDigits) {
  if (value === undefined || value === null || value === '') return '-';
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return '-';
  return parsed.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });
}

// Converte entrada decimal pt-BR ("1,5") em número; null se vazia ou inválida.
function parseDecimalInput(value) {
  if (value === undefined || value === null || String(value).trim() === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

// Permite apenas dígitos, vírgula/ponto e teclas de navegação nos campos decimais.
function isAllowedDecimalKey(event) {
  if (event.ctrlKey || event.metaKey || event.altKey) return true;
  const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Enter'];
  return allowedKeys.includes(event.key) || /[\d,.]/.test(event.key);
}

// Concretagem da API -> { id, data, idObra, obra, flagRevisao }.
function normalizarConcretagem(item) {
  return {
    id: String(item.idConcretagemRealizada ?? item.id ?? ''),
    data: normalizeDateOnly(item.dataConcretagem || item.data_concretagem || ''),
    idObra: String(item.obra?.idObra ?? item.idObra ?? ''),
    obra: item.obra?.nomeObra || item.nomeObra || '-',
    flagRevisao: Boolean(item.obra?.flagReviResuCorpoProva ?? item.obra?.flag_revi_resu_corpo_prova),
  };
}

// Caminhão de /api/resultadoCorpoProva/revisao -> { id, placaCaminhao, notaFiscal, fck, ponderadorTipoCimento, corposProva }.
function normalizarCaminhao(item) {
  const notaFiscal = [item.numrNotaFiscalPrincipal, item.numrNotaFiscalSecundaria]
    .map((valor) => String(valor ?? '').trim())
    .find((valor) => valor && valor !== '0');
  const fck = Number(item.valrClasResiCompressao);
  const ponderador = item.tipoCimento?.valorPonderador;
  return {
    id: String(item.idCaminhao ?? item.id ?? ''),
    placaCaminhao: item.placaCaminhao || '',
    notaFiscal: notaFiscal || '-',
    // O cadastro do caminhão grava fck 0 quando o campo fica vazio: tratado como sem fck.
    fck: Number.isFinite(fck) && fck > 0 ? fck : null,
    ponderadorTipoCimento: ponderador != null && Number.isFinite(Number(ponderador)) ? Number(ponderador) : null,
    corposProva: Array.isArray(item.corposProva) ? item.corposProva : [],
  };
}

// Corpo de prova do caminhão (com o resultado mais recente, ou null) -> linha da tela.
function normalizarLinha(cp, caminhao) {
  const resultado = cp.resultado || null;
  const idCorpoProva = String(cp.idCorpoProva ?? '');
  const etiqueta = String(cp.idenObraEtiqueta || '-');
  const cargaAtual = resultado?.valrCarga ?? null;
  const referencia = { fck: caminhao.fck, idade: cp.idadeRuptura, ponderadorTipoCimento: caminhao.ponderadorTipoCimento };

  return {
    id: idCorpoProva || `${caminhao.id}-${etiqueta}`,
    idCaminhao: caminhao.id,
    notaFiscal: caminhao.notaFiscal,
    etiqueta,
    idade: cp.idadeRuptura ?? null,
    idResultado: resultado?.idResuCorpoProva != null ? String(resultado.idResuCorpoProva) : '',
    cargaAtual,
    cargaCorrigidaSalva: resultado?.valrCargaCorrigida ?? null,
    ponderadorTipoCarga: resultado?.valrPonderadorTipoCarga ?? null,
    ponderadorTipoCorpoProva: cp.valrPonderadorTipoCorpoProva ?? null,
    // Referências da idade de ruptura; não dependem da carga, então existem também para CPs pendentes.
    fcjEsperado: calcularFcjEsperado(referencia),
    fcmj: calcularFcmj(referencia),
    temResultado: cargaAtual != null,
  };
}

// Resultado (MPa) para uma carga, com os ponderadores do tipo de carga e do tipo de CP.
function resultadoDaCarga(linha, carga) {
  return calcularFcj({
    valrCarga: carga,
    ponderadorTipoCarga: linha.ponderadorTipoCarga,
    ponderadorTipoCorpoProva: linha.ponderadorTipoCorpoProva,
  });
}

// Revisão pendente: algum CP com resultado lançado e ainda sem carga corrigida salva.
function temRevisaoPendente(linhas) {
  return linhas.some((linha) => linha.temResultado && linha.cargaCorrigidaSalva == null);
}

// Linhas (CPs) de todos os caminhões de uma resposta de /revisao.
function linhasDaRevisao(data) {
  return (Array.isArray(data?.caminhoes) ? data.caminhoes : [])
    .map(normalizarCaminhao)
    .flatMap((caminhao) => caminhao.corposProva.map((cp) => normalizarLinha(cp, caminhao)));
}

// Rótulo da nota fiscal do caminhão no select e nos títulos.
function rotuloNotaFiscal(caminhao) {
  const nf = caminhao.notaFiscal && caminhao.notaFiscal !== '-' ? `NF ${caminhao.notaFiscal}` : 'Sem nota fiscal';
  return caminhao.placaCaminhao ? `${nf} - ${caminhao.placaCaminhao}` : nf;
}

// Barra com os cantos de cima arredondados e a base reta, apoiada no eixo.
function caminhoBarra(x, y, largura, altura, raio) {
  const r = Math.min(raio, altura, largura / 2);
  return `M${x},${y + altura} V${y + r} Q${x},${y} ${x + r},${y} H${x + largura - r} Q${x + largura},${y} ${x + largura},${y + r} V${y + altura} Z`;
}

// Teto "redondo" do eixo Y e o passo entre as linhas de grade (~4 divisões).
function escalaY(maximo) {
  if (!maximo || maximo <= 0) return { teto: 10, passo: 2.5 };
  const bruto = maximo / 4;
  const magnitude = 10 ** Math.floor(Math.log10(bruto));
  const passo = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((p) => p >= bruto);
  return { teto: Math.ceil(maximo / passo) * passo, passo };
}

const GRAFICO = { esquerda: 44, direita: 12, topo: 36, base: 72, altura: 340, grupoMin: 96, grupoMax: 220, barraMax: 40, espaco: 2 };

// Séries na ordem das barras de cada CP; classes escritas por extenso para o Tailwind gerá-las.
const SERIES_GRAFICO = [
  { chave: 'fcjEsperado', rotulo: 'Fcj esperado', valorDe: (l) => l.fcjEsperado, barra: 'fill-[#dc2626]', ativa: 'fill-[#b91c1c]', amostra: 'bg-[#dc2626]' },
  { chave: 'resultado', rotulo: 'Resultado corrigido', valorDe: (l) => l.resultadoCorrigido, barra: 'fill-[#0e7490]', ativa: 'fill-[#0c6279]', amostra: 'bg-[#0e7490]' },
  { chave: 'fcmj', rotulo: 'Fcmj', valorDe: (l) => l.fcmj, barra: 'fill-[#16a34a]', ativa: 'fill-[#15803d]', amostra: 'bg-[#16a34a]' },
];

// Largura do elemento do ref, medida antes da pintura e atualizada quando o layout muda (0 até medir).
function useLargura() {
  const ref = useRef(null);
  const [largura, setLargura] = useState(0);

  useLayoutEffect(() => {
    const elemento = ref.current;
    if (!elemento) return undefined;
    setLargura(elemento.clientWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entrada]) => setLargura(Math.floor(entrada.contentRect.width)));
    observer.observe(elemento);
    return () => observer.disconnect();
  }, []);

  return [ref, largura];
}

// Linhas do tooltip de um CP: uma por série (com a cor), o original logo após o resultado e a carga.
function itensTooltip(linha, series) {
  const itens = [];
  series.forEach((serie) => {
    const valor = serie.valorDe(linha);
    if (serie.chave !== 'resultado') {
      itens.push({ serie, texto: `${serie.rotulo}: ${valor != null ? `${formatDecimal(valor, 2)} MPa` : '-'}` });
      return;
    }
    itens.push({ serie, texto: linha.temResultado ? `Resultado: ${formatDecimal(valor, 2)} MPa` : 'Resultado ainda não lançado' });
    if (linha.alterado) itens.push({ texto: `Original: ${formatDecimal(linha.resultadoOriginal, 2)} MPa` });
  });
  if (linha.temResultado) itens.push({ texto: `Carga: ${formatDecimal(linha.cargaEfetiva, 3)}` });
  return itens;
}

// Gráfico de barras agrupadas por CP da nota: Fcj esperado | resultado corrigido | Fcmj, no mesmo eixo.
// O tracejado marca o resultado original quando a revisão alterou a carga.
function GraficoResultados({ linhas, caminhao }) {
  const [destaque, setDestaque] = useState(null);
  const [containerRef, larguraDisponivel] = useLargura();

  // Sem fck ou S não há referência calculável: fica só o resultado.
  const semReferencia = caminhao.fck == null || caminhao.ponderadorTipoCimento == null;
  const series = semReferencia ? SERIES_GRAFICO.filter((s) => s.chave === 'resultado') : SERIES_GRAFICO;

  const maximoDoGrupo = (linha) => Math.max(0, ...series.map((s) => s.valorDe(linha) ?? 0));
  const { teto, passo } = escalaY(Math.max(0, ...linhas.map((l) => Math.max(maximoDoGrupo(l), l.resultadoOriginal ?? 0))));
  const quantidade = Math.max(linhas.length, 1);
  const areaUtil = larguraDisponivel - GRAFICO.esquerda - GRAFICO.direita;
  const grupo = Math.min(GRAFICO.grupoMax, Math.max(GRAFICO.grupoMin, areaUtil / quantidade));
  const barra = Math.min(GRAFICO.barraMax, Math.floor((grupo * 0.7 - GRAFICO.espaco * (series.length - 1)) / series.length));
  const larguraBarras = series.length * barra + (series.length - 1) * GRAFICO.espaco;
  const largura = Math.max(larguraDisponivel, GRAFICO.esquerda + quantidade * grupo + GRAFICO.direita);
  // Com poucos CPs o grupo chega no máximo; o conjunto fica centralizado na área do gráfico.
  const inicio = GRAFICO.esquerda + Math.max(0, (largura - GRAFICO.esquerda - GRAFICO.direita - quantidade * grupo) / 2);
  const alturaTotal = GRAFICO.topo + GRAFICO.altura + GRAFICO.base;
  const baseY = GRAFICO.topo + GRAFICO.altura;
  const y = (valor) => baseY - (valor / teto) * GRAFICO.altura;
  const ticks = Array.from({ length: Math.round(teto / passo) + 1 }, (_, i) => i * passo);
  // Barras estreitas não comportam três números lado a lado: aí só o resultado ganha rótulo.
  const rotulaTodas = series.length === 1 || barra >= 30;
  const temOriginalDiferente = linhas.some((l) => l.alterado);

  const indiceDestaque = linhas.findIndex((l) => l.id === destaque);
  const linhaDestaque = linhas[indiceDestaque];

  const subtitulo = [
    rotuloNotaFiscal(caminhao),
    'valores em MPa',
    caminhao.fck != null ? `fck ${formatDecimal(caminhao.fck, 1)} MPa` : null,
    caminhao.ponderadorTipoCimento != null ? `S ${formatDecimal(caminhao.ponderadorTipoCimento, 3)}` : null,
  ].filter(Boolean).join(' · ');

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div>
          <p className="text-base font-semibold text-slate-900">Resultados por corpo de prova</p>
          <p className="text-xs text-slate-500">{subtitulo}</p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
          {series.map((serie) => (
            <span key={serie.chave} className="inline-flex items-center gap-1.5">
              <span className={`h-2.5 w-2.5 rounded-sm ${serie.amostra}`} />
              {serie.rotulo}
            </span>
          ))}
          {temOriginalDiferente ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="w-4 border-t-2 border-dashed border-slate-400" />
              Original
            </span>
          ) : null}
        </div>
      </div>
      {semReferencia ? (
        <p className="mt-2 text-xs text-amber-700">
          Sem fck ou ponderador do tipo de cimento: Fcj esperado e Fcmj não calculados.
        </p>
      ) : null}

      <div ref={containerRef} className="mt-5 overflow-x-auto" style={{ minHeight: alturaTotal }}>
        {larguraDisponivel > 0 ? (
          <svg
            width={largura}
            height={alturaTotal}
            viewBox={`0 0 ${largura} ${alturaTotal}`}
            role="img"
            aria-label={`Gráfico de barras dos corpos de prova da ${rotuloNotaFiscal(caminhao)}: ${series.map((s) => s.rotulo).join(', ')}`}
            className="block"
          >
            <g className="grafico-revisao__grade">
            {ticks.map((tick) => (
              <g key={tick}>
                <line
                  x1={GRAFICO.esquerda}
                  x2={largura - GRAFICO.direita}
                  y1={y(tick)}
                  y2={y(tick)}
                  className={tick === 0 ? 'stroke-slate-300' : 'stroke-slate-100'}
                />
                <text x={GRAFICO.esquerda - 8} y={y(tick) + 4} textAnchor="end" className="fill-slate-400 text-[12px]">
                  {formatDecimal(tick, 1)}
                </text>
              </g>
            ))}
            </g>

            {/* Grupo próprio para o :nth-child do CSS escalonar só as colunas. */}
            <g>
            {linhas.map((linha, indice) => {
              const xGrupo = inicio + indice * grupo;
              const xPrimeira = xGrupo + (grupo - larguraBarras) / 2;
              const centro = xGrupo + grupo / 2;
              const ativo = destaque === linha.id;

              return (
                <g
                  key={linha.id}
                  tabIndex={0}
                  onMouseEnter={() => setDestaque(linha.id)}
                  onMouseLeave={() => setDestaque(null)}
                  onFocus={() => setDestaque(linha.id)}
                  onBlur={() => setDestaque(null)}
                  className="grafico-revisao__coluna focus:outline-none"
                >
                  <rect
                    x={xGrupo + 4}
                    y={GRAFICO.topo - 8}
                    width={grupo - 8}
                    height={GRAFICO.altura + 8}
                    rx={8}
                    className={`grafico-revisao__destaque ${ativo ? 'fill-slate-50' : 'fill-transparent'}`}
                  />
                  {series.map((serie, i) => {
                    const valor = serie.valorDe(linha);
                    if (valor == null) return null;
                    const xBarra = xPrimeira + i * (barra + GRAFICO.espaco);
                    const ehResultado = serie.chave === 'resultado';
                    // Rótulo único do resultado sobe acima da barra mais alta do CP para não invadir as vizinhas.
                    const yRotulo = rotulaTodas ? y(valor) - 8 : y(maximoDoGrupo(linha)) - 8;

                    return (
                      <g key={serie.chave}>
                        <path
                          d={caminhoBarra(xBarra, y(valor), barra, baseY - y(valor), 4)}
                          className={`grafico-revisao__barra ${ativo ? serie.ativa : serie.barra}`}
                        />
                        {rotulaTodas || ehResultado ? (
                          <text
                            x={xBarra + barra / 2}
                            y={yRotulo}
                            textAnchor="middle"
                            className={`grafico-revisao__valor font-medium ${ehResultado ? 'fill-slate-800 text-[13px]' : 'fill-slate-500 text-[12px]'}`}
                          >
                            {formatDecimal(valor, 1)}
                          </text>
                        ) : null}
                        {ehResultado && linha.alterado && linha.resultadoOriginal != null ? (
                          <line
                            x1={xBarra - 4}
                            x2={xBarra + barra + 4}
                            y1={y(linha.resultadoOriginal)}
                            y2={y(linha.resultadoOriginal)}
                            strokeWidth={2}
                            strokeDasharray="4 3"
                            className="grafico-revisao__original stroke-slate-400"
                          />
                        ) : null}
                      </g>
                    );
                  })}
                  <text x={centro} y={baseY + 22} textAnchor="middle" className="fill-slate-600 text-[13px] font-medium">
                    {linha.etiqueta}
                  </text>
                  <text x={centro} y={baseY + 40} textAnchor="middle" className="fill-slate-400 text-[12px]">
                    {linha.idade != null ? `${linha.idade} dias` : '-'}
                  </text>
                  {!linha.temResultado ? (
                    <text x={centro} y={baseY + 58} textAnchor="middle" className="grafico-revisao__pendente fill-slate-400 text-[12px] italic">
                      pendente
                    </text>
                  ) : null}
                </g>
              );
            })}
            </g>

            {linhaDestaque ? (() => {
              const itens = itensTooltip(linhaDestaque, series);
              const tooltipLargura = 212;
              const tooltipAltura = 34 + itens.length * 18;
              const centro = inicio + indiceDestaque * grupo + grupo / 2;
              const x = Math.min(Math.max(centro - tooltipLargura / 2, 4), largura - tooltipLargura - 4);
              const topoGrupo = y(Math.max(maximoDoGrupo(linhaDestaque), linhaDestaque.resultadoOriginal ?? 0));
              const yTooltip = Math.max(4, topoGrupo - tooltipAltura - 26);

              return (
                <g key={linhaDestaque.id} className="grafico-revisao__tooltip pointer-events-none">
                  <rect x={x} y={yTooltip} width={tooltipLargura} height={tooltipAltura} rx={8} className="fill-white stroke-slate-200" />
                  <text x={x + 12} y={yTooltip + 20} className="fill-slate-900 text-[13px] font-semibold">
                    {linhaDestaque.etiqueta}
                    {linhaDestaque.idade != null ? ` · ${linhaDestaque.idade} dias` : ''}
                  </text>
                  {itens.map((item, i) => {
                    const yItem = yTooltip + 40 + i * 18;
                    return (
                      <g key={item.texto}>
                        {item.serie ? <rect x={x + 12} y={yItem - 9} width={9} height={9} rx={2} className={item.serie.barra} /> : null}
                        <text x={x + 28} y={yItem} className="fill-slate-600 text-[12px]">
                          {item.texto}
                        </text>
                      </g>
                    );
                  })}
                </g>
              );
            })() : null}
          </svg>
        ) : null}
      </div>
    </div>
  );
}

// Selo do estado da linha: pendente (sem resultado), alteração não salva, revisado ou lançado.
function StatusLinha({ linha }) {
  if (!linha.temResultado) {
    return <span className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">Pendente</span>;
  }
  if (linha.sujo) {
    return <span className="whitespace-nowrap rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">Não salvo</span>;
  }
  if (linha.cargaCorrigidaSalva != null) {
    return <span className="whitespace-nowrap rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">Revisado</span>;
  }
  return <span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Lançado</span>;
}

// Revisão de resultados: para obras com "Revisar resultado CP" marcado, lista os CPs da concretagem
// (lançados e pendentes), permite corrigir a carga e mostra o gráfico dos CPs da nota fiscal escolhida.
function RevisaoResultadosPage() {
  const role = getSessionRole();
  const canManage = role === 'admin';

  const [loading, setLoading] = useState(true);
  const [loadingDados, setLoadingDados] = useState(false);
  const [erro, setErro] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [filtros, setFiltros] = useState(INITIAL_FILTROS);

  const [concretagens, setConcretagens] = useState([]);
  const [caminhoes, setCaminhoes] = useState([]);
  const [linhasBase, setLinhasBase] = useState([]);
  // Carga corrigida digitada, por id da linha (string como o usuário digitou).
  const [edicoes, setEdicoes] = useState({});
  // Flag "só com resultados a revisar" e, por id da concretagem, se há revisão pendente (null = não consultado).
  const [somentePendentes, setSomentePendentes] = useState(false);
  const [statusRevisao, setStatusRevisao] = useState(null);
  const [loadingStatus, setLoadingStatus] = useState(false);

  // Carrega concretagens e obras; só entram concretagens de obras que exigem revisão de resultado.
  useEffect(() => {
    let ativo = true;

    const carregar = async () => {
      const [concretagensResult, obrasResult] = await Promise.allSettled([
        getConcretagemRealizada(),
        getObras(),
      ]);

      if (!ativo) return;

      if (concretagensResult.status === 'rejected') {
        setErro('Não foi possível carregar as concretagens. Tente novamente.');
      }

      const obrasComRevisao = new Set(
        (Array.isArray(obrasResult.value) ? obrasResult.value : [])
          .filter((obra) => Boolean(obra.flagReviResuCorpoProva ?? obra.flag_revi_resu_corpo_prova))
          .map((obra) => String(obra.idObra ?? obra.id_obra ?? obra.id ?? ''))
      );

      const lista = Array.isArray(concretagensResult.value) ? concretagensResult.value : [];
      setConcretagens(
        lista
          .map(normalizarConcretagem)
          .filter((item) => item.id && (item.flagRevisao || obrasComRevisao.has(item.idObra)))
          .sort((a, b) => String(b.data).localeCompare(String(a.data)))
      );
      setLoading(false);
    };

    carregar().catch(() => {
      if (!ativo) return;
      setErro('Não foi possível carregar as concretagens. Tente novamente.');
      setLoading(false);
    });

    return () => { ativo = false; };
  }, []);

  // Na primeira vez que a flag é ligada, consulta a revisão de cada concretagem para saber quais têm
  // resultado sem revisão. Concretagem que falhar fica como null e continua aparecendo.
  useEffect(() => {
    if (!somentePendentes || statusRevisao || concretagens.length === 0) return undefined;

    let ativo = true;
    setLoadingStatus(true);

    Promise.allSettled(concretagens.map((item) => getRevisaoResultado(item.id)))
      .then((resultados) => {
        if (!ativo) return;
        setStatusRevisao(Object.fromEntries(resultados.map((resultado, indice) => [
          concretagens[indice].id,
          resultado.status === 'fulfilled' ? temRevisaoPendente(linhasDaRevisao(resultado.value)) : null,
        ])));
        setLoadingStatus(false);
      });

    return () => {
      ativo = false;
      setLoadingStatus(false);
    };
  }, [somentePendentes, statusRevisao, concretagens]);

  // Ao escolher a concretagem, busca os caminhões (notas fiscais), seus CPs e os resultados lançados.
  useEffect(() => {
    setCaminhoes([]);
    setLinhasBase([]);
    setEdicoes({});
    setFormErrors({});
    if (!filtros.concretagem) return undefined;

    let ativo = true;
    setLoadingDados(true);
    setErro('');

    getRevisaoResultado(filtros.concretagem)
      .then((data) => {
        if (!ativo) return;
        const lista = (Array.isArray(data?.caminhoes) ? data.caminhoes : [])
          .map(normalizarCaminhao)
          .filter((c) => c.id)
          .sort((a, b) => String(a.notaFiscal).localeCompare(String(b.notaFiscal), 'pt-BR', { numeric: true }));
        const novasLinhas = lista.flatMap((caminhao) => caminhao.corposProva
          .map((cp) => normalizarLinha(cp, caminhao))
          .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'pt-BR', { numeric: true })));
        setCaminhoes(lista);
        setLinhasBase(novasLinhas);
        // Aproveita a consulta para manter o status da flag em dia.
        setStatusRevisao((prev) => (prev ? { ...prev, [filtros.concretagem]: temRevisaoPendente(novasLinhas) } : prev));
      })
      .catch((error) => {
        if (ativo) setErro(error?.message || 'Não foi possível carregar os resultados desta concretagem. Tente novamente.');
      })
      .finally(() => {
        if (ativo) setLoadingDados(false);
      });

    return () => { ativo = false; };
  }, [filtros.concretagem]);

  // Linhas com os valores derivados da edição: carga efetiva, resultado original x corrigido e se há alteração pendente.
  const linhas = useMemo(() => linhasBase.map((linha) => {
    const editando = Object.prototype.hasOwnProperty.call(edicoes, linha.id);
    const cargaDigitada = editando ? parseDecimalInput(edicoes[linha.id]) : linha.cargaCorrigidaSalva;
    const cargaEfetiva = cargaDigitada ?? linha.cargaAtual;
    const resultadoOriginal = resultadoDaCarga(linha, linha.cargaAtual);
    const resultadoCorrigido = resultadoDaCarga(linha, cargaEfetiva);
    const sujo = editando && cargaDigitada !== (linha.cargaCorrigidaSalva ?? null);

    return {
      ...linha,
      // Sem separador de milhar, para o valor continuar editável/parseável.
      valorInput: editando ? edicoes[linha.id] : String(linha.cargaCorrigidaSalva ?? '').replace('.', ','),
      cargaEfetiva,
      resultadoOriginal,
      resultadoCorrigido,
      alterado: cargaDigitada != null && cargaDigitada !== linha.cargaAtual,
      sujo,
    };
  }), [linhasBase, edicoes]);

  const linhasFiltradas = useMemo(
    () => (filtros.caminhao ? linhas.filter((linha) => linha.idCaminhao === filtros.caminhao) : linhas),
    [linhas, filtros.caminhao]
  );

  const caminhaoSelecionado = caminhoes.find((c) => c.id === filtros.caminhao) || null;

  // Com a flag, só concretagens com revisão pendente; a que está aberta fica na lista para o select não esvaziar.
  const concretagensVisiveis = useMemo(() => (somentePendentes
    ? concretagens.filter((item) => statusRevisao?.[item.id] !== false || item.id === filtros.concretagem)
    : concretagens), [concretagens, somentePendentes, statusRevisao, filtros.concretagem]);

  const totais = useMemo(() => ({
    lancados: linhasFiltradas.filter((l) => l.temResultado).length,
    pendentes: linhasFiltradas.filter((l) => !l.temResultado).length,
    revisados: linhasFiltradas.filter((l) => l.cargaCorrigidaSalva != null).length,
  }), [linhasFiltradas]);

  const linhasSujas = linhas.filter((l) => l.sujo);

  // Troca de concretagem descarta a revisão não salva, então pede confirmação.
  const handleFiltroConcretagem = (value) => {
    if (linhasSujas.length > 0 && !window.confirm('Existem cargas corrigidas não salvas. Deseja descartá-las?')) return;
    setFiltros({ concretagem: value, caminhao: '' });
  };
  const handleFiltroCaminhao = (value) => setFiltros((prev) => ({ ...prev, caminhao: value }));

  const handleCargaChange = (id) => (event) => {
    const { value } = event.target;
    setEdicoes((prev) => ({ ...prev, [id]: value }));
    setFormErrors((prev) => {
      if (!prev[id] && !prev.geral) return prev;
      const next = { ...prev };
      delete next[id];
      delete next.geral;
      return next;
    });
  };

  const descartarEdicoes = () => {
    setEdicoes({});
    setFormErrors({});
  };

  // Mesmos limites do lançamento de resultado: 0 a 9999,999 com até 3 casas.
  const validar = () => {
    const nextErrors = {};
    linhasSujas.forEach((linha) => {
      const texto = String(edicoes[linha.id] ?? '').trim();
      if (!texto) return;
      const valor = parseDecimalInput(texto);
      const decimais = texto.split(/[.,]/)[1] || '';
      if (valor == null) nextErrors[linha.id] = 'Carga inválida.';
      else if (valor < 0 || valor > 9999.999) nextErrors[linha.id] = 'Entre 0 e 9999,999.';
      else if (decimais.length > 3) nextErrors[linha.id] = 'Até 3 casas decimais.';
      else if (!linha.idResultado) nextErrors[linha.id] = 'Resultado sem identificador.';
    });
    return nextErrors;
  };

  // Envia todas as cargas alteradas em uma requisição e consolida como "salvas" localmente.
  const [salvando, handleSalvar] = useSubmitLock(async () => {
    const nextErrors = validar();
    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      return;
    }

    try {
      const itens = linhasSujas.map((linha) => ({
        idResultado: linha.idResultado,
        cargaCorrigida: edicoes[linha.id],
      }));
      await salvarRevisaoResultado(filtros.concretagem, itens);

      const salvos = Object.fromEntries(linhasSujas.map((linha) => [linha.id, parseDecimalInput(edicoes[linha.id])]));
      const atualizadas = linhasBase.map((linha) => (
        Object.prototype.hasOwnProperty.call(salvos, linha.id) ? { ...linha, cargaCorrigidaSalva: salvos[linha.id] } : linha
      ));
      setLinhasBase(atualizadas);
      setStatusRevisao((prev) => (prev ? { ...prev, [filtros.concretagem]: temRevisaoPendente(atualizadas) } : prev));
      setEdicoes({});
      setFormErrors({});
      showSuccessToast('Revisão salva com sucesso.');
    } catch (error) {
      setFormErrors({ geral: error?.message || 'Não foi possível salvar a revisão.' });
    }
  });

  const selectClass = 'w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 disabled:cursor-not-allowed disabled:opacity-60';
  const inputCargaClass = (id) => `w-28 rounded-lg border bg-slate-50 px-2 py-1 text-sm text-slate-700 focus:outline-none focus:ring-2 disabled:cursor-not-allowed disabled:opacity-50 ${formErrors[id]
    ? 'border-rose-300 focus:border-rose-500 focus:ring-rose-500/20'
    : 'border-slate-200 focus:border-blue-500 focus:ring-blue-500/20'}`;

  const mostraColunaNf = !filtros.caminhao;
  const totalColunas = mostraColunaNf ? 7 : 6;

  // Campo de carga corrigida (ou só o valor, para quem não pode revisar).
  const renderCampoCarga = (linha) => {
    if (!linha.temResultado) return <span className="text-slate-400">-</span>;
    if (!canManage) return <span className="text-slate-600">{formatDecimal(linha.cargaCorrigidaSalva, 3)}</span>;
    return (
      <div>
        <input
          type="text"
          inputMode="decimal"
          aria-label={`Carga corrigida do corpo de prova ${linha.etiqueta}`}
          value={linha.valorInput}
          onChange={handleCargaChange(linha.id)}
          onKeyDown={(event) => {
            if (!isAllowedDecimalKey(event)) event.preventDefault();
          }}
          placeholder={formatDecimal(linha.cargaAtual, 3)}
          className={inputCargaClass(linha.id)}
        />
        {formErrors[linha.id] ? <p className="mt-1 text-xs text-rose-600">{formErrors[linha.id]}</p> : null}
      </div>
    );
  };

  return (
    <div className="page-content-enter space-y-4">
      <header className="space-y-1">
        <h1 className="text-xl font-semibold text-slate-900">Revisão de resultados</h1>
        <p className="text-sm text-slate-500">
          Confira os resultados lançados nas obras que exigem revisão e corrija a carga quando necessário.
        </p>
      </header>

      <section className="flex flex-wrap gap-8">
        {[
          { rotulo: 'Resultados lançados', valor: totais.lancados },
          { rotulo: 'Pendentes', valor: totais.pendentes },
          { rotulo: 'Revisados', valor: totais.revisados },
        ].map((total) => (
          <div
            key={total.rotulo}
            className="min-w-[14rem] rounded-2xl border border-slate-200 bg-white px-6 py-5 shadow-sm"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400">{total.rotulo}</p>
            <p className="mt-2 text-3xl font-semibold text-slate-900">{filtros.concretagem ? total.valor : '-'}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <label htmlFor="filtro-concretagem" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Concretagem realizada
            </label>
            <select
              id="filtro-concretagem"
              value={filtros.concretagem}
              onChange={(event) => handleFiltroConcretagem(event.target.value)}
              disabled={loading}
              className={selectClass}
            >
              <option value="">
                {loading
                  ? 'Carregando...'
                  : somentePendentes && loadingStatus
                    ? 'Verificando revisões...'
                    : concretagensVisiveis.length === 0
                      ? (somentePendentes ? 'Nenhuma concretagem com resultado a revisar' : 'Nenhuma concretagem com revisão')
                      : 'Selecione'}
              </option>
              {concretagensVisiveis.map((item) => (
                <option key={item.id} value={item.id}>
                  {formatDateBr(item.data)} - {item.obra}
                </option>
              ))}
            </select>
            <label className="inline-flex cursor-pointer items-center gap-2 pt-1 text-sm text-slate-600">
              <input
                type="checkbox"
                checked={somentePendentes}
                onChange={(event) => setSomentePendentes(event.target.checked)}
                disabled={loading}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500/20"
              />
              Só concretagens com resultados a revisar
            </label>
          </div>
          <div className="space-y-1">
            <label htmlFor="filtro-nota-fiscal" className="text-xs font-medium uppercase tracking-[0.2em] text-slate-400">
              Nota fiscal
            </label>
            <select
              id="filtro-nota-fiscal"
              value={filtros.caminhao}
              onChange={(event) => handleFiltroCaminhao(event.target.value)}
              disabled={!filtros.concretagem || loadingDados}
              className={selectClass}
            >
              <option value="">{filtros.concretagem ? 'Todas' : 'Selecione a concretagem'}</option>
              {caminhoes.map((caminhao) => (
                <option key={caminhao.id} value={caminhao.id}>{rotuloNotaFiscal(caminhao)}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          Só aparecem concretagens de obras com a opção “Revisar resultado CP” marcada.
        </p>
      </section>

      {erro ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{erro}</p>
      ) : null}

      {caminhaoSelecionado && !loadingDados ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
          {linhasFiltradas.length === 0 ? (
            <p className="text-sm text-slate-500">Nenhum corpo de prova cadastrado nesta nota fiscal.</p>
          ) : (
            // key: trocar de nota remonta o gráfico e a animação de entrada roda de novo.
            <GraficoResultados
              key={caminhaoSelecionado.id}
              linhas={linhasFiltradas}
              caminhao={caminhaoSelecionado}
            />
          )}
        </section>
      ) : null}

      {filtros.concretagem ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          {formErrors.geral ? (
            <div className="border-b border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{formErrors.geral}</div>
          ) : null}

          {canManage && linhasSujas.length > 0 ? (
            <div className="flex flex-col gap-2 border-b border-slate-200 bg-blue-50/60 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-slate-700">
                {linhasSujas.length} carga(s) corrigida(s) não salva(s).
              </p>
              <div className="flex flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={descartarEdicoes}
                  disabled={salvando}
                  className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-600 transition hover:border-blue-200 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <RotateCcw className="h-4 w-4" />
                  Descartar
                </button>
                <button
                  type="button"
                  onClick={handleSalvar}
                  disabled={salvando}
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Save className="h-4 w-4" />
                  {salvando ? 'Salvando...' : 'Salvar revisão'}
                </button>
              </div>
            </div>
          ) : null}

          <div className="hidden overflow-x-auto sm:block">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-[0.3em] text-slate-400">
                <tr>
                  {mostraColunaNf ? <th className="px-4 py-3">Nota fiscal</th> : null}
                  <th className="px-4 py-3">Etiqueta</th>
                  <th className="px-4 py-3">Idade</th>
                  <th className="px-4 py-3">Carga atual</th>
                  <th className="px-4 py-3">Carga corrigida</th>
                  <th className="px-4 py-3">Resultado corrigido</th>
                  <th className="px-4 py-3">
                    <span className="flex justify-end normal-case tracking-normal">
                      Resultados: {linhasFiltradas.length}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {loadingDados ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={`sk-${idx}`} className="border-b border-slate-100">
                      {Array.from({ length: totalColunas }).map((__, cell) => (
                        <td key={cell} className="px-4 py-4">
                          <div className="h-4 w-full animate-pulse rounded bg-slate-100" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : linhasFiltradas.length === 0 ? (
                  <tr>
                    <td colSpan={totalColunas} className="px-4 py-10 text-center text-sm text-slate-500">
                      Nenhum corpo de prova encontrado para esta concretagem.
                    </td>
                  </tr>
                ) : (
                  linhasFiltradas.map((linha) => (
                    <tr key={linha.id} className="border-b border-slate-100 align-top last:border-0 hover:bg-slate-50/80">
                      {mostraColunaNf ? (
                        <td className="px-4 py-3 text-slate-600">
                          {linha.notaFiscal && linha.notaFiscal !== '-' ? linha.notaFiscal : 'Sem NF'}
                        </td>
                      ) : null}
                      <td className="px-4 py-3 font-medium text-slate-900">{linha.etiqueta}</td>
                      <td className="px-4 py-3 text-slate-600">{linha.idade != null ? `${linha.idade} dias` : '-'}</td>
                      <td className="px-4 py-3 text-slate-600">{formatDecimal(linha.cargaAtual, 3)}</td>
                      <td className="px-4 py-2">{renderCampoCarga(linha)}</td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-slate-900">{formatDecimal(linha.resultadoCorrigido, 2)}</span>
                        {linha.resultadoCorrigido != null ? <span className="ml-1 text-xs text-slate-400">MPa</span> : null}
                        {linha.alterado && linha.resultadoOriginal != null ? (
                          <p className="text-xs text-slate-400">era {formatDecimal(linha.resultadoOriginal, 2)}</p>
                        ) : null}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end">
                          <StatusLinha linha={linha} />
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile: cada corpo de prova vira um cartão com os mesmos campos da tabela. */}
          <div className="divide-y divide-slate-100 sm:hidden">
            {loadingDados ? (
              Array.from({ length: 3 }).map((_, idx) => (
                <div key={`skm-${idx}`} className="space-y-2 p-4">
                  <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
                  <div className="h-4 w-1/3 animate-pulse rounded bg-slate-100" />
                </div>
              ))
            ) : linhasFiltradas.length === 0 ? (
              <p className="p-6 text-center text-sm text-slate-500">
                Nenhum corpo de prova encontrado para esta concretagem.
              </p>
            ) : (
              linhasFiltradas.map((linha) => (
                <div key={linha.id} className="space-y-3 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-medium text-slate-900">{linha.etiqueta}</p>
                      <p className="text-xs text-slate-500">
                        {linha.idade != null ? `${linha.idade} dias` : '-'}
                        {mostraColunaNf ? ` · ${linha.notaFiscal && linha.notaFiscal !== '-' ? `NF ${linha.notaFiscal}` : 'Sem NF'}` : ''}
                      </p>
                    </div>
                    <StatusLinha linha={linha} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-xs text-slate-400">Carga atual</p>
                      <p className="text-slate-700">{formatDecimal(linha.cargaAtual, 3)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-400">Resultado corrigido</p>
                      <p className="font-medium text-slate-900">
                        {formatDecimal(linha.resultadoCorrigido, 2)}
                        {linha.resultadoCorrigido != null ? <span className="ml-1 text-xs font-normal text-slate-400">MPa</span> : null}
                      </p>
                    </div>
                  </div>
                  {linha.temResultado ? (
                    <div>
                      <p className="mb-1 text-xs text-slate-400">Carga corrigida</p>
                      {renderCampoCarga(linha)}
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </section>
      ) : !loading ? (
        <section className="rounded-2xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500">
          Selecione uma concretagem para ver os resultados.
        </section>
      ) : null}
    </div>
  );
}

export default RevisaoResultadosPage;
